// ─────────────────────────────────────────────────────────────────────────────
// /title-chart/:token — the chain of title, for an agent with no account
// ─────────────────────────────────────────────────────────────────────────────
// A Shadowland Title agent opens this from the assignment and draws the chain
// on Paraclete's own chart. Not a copy that gets sent back: the same
// canvas_diagrams row the Confirmation Title checklist item opens, so the
// work appears on Mays's screen as it happens.
//
// The token is the whole of the agent's access, so this page asks the server
// for everything and holds no Supabase session. It cannot read a deal it was
// not given, and the routes behind it cannot either.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Map as MapIcon, AlertTriangle } from 'lucide-react';

import SavedDiagram from '../components/canvas/SavedDiagram';

const NAVY = '#003279';

async function api(path, { method = 'GET', body = null } = {}) {
  const res = await fetch(path, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let payload = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = { error: text }; }
  if (!res.ok) {
    const err = new Error(payload?.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

function tractLabel(tract) {
  const plss = [
    tract.section ? `Sec ${tract.section}` : '',
    tract.township, tract.range,
  ].filter(Boolean).join(' ');
  const body = plss || [tract.survey, tract.abstractNo ? `A-${tract.abstractNo}` : '', tract.block]
    .filter(Boolean).join(' ');
  const where = [tract.county ? `${tract.county} County` : '', tract.state].filter(Boolean).join(', ');
  return [tract.quarterCall, body, where].filter(Boolean).join(' · ') || tract.description || 'Unnamed tract';
}

export default function TitleChartShare() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // null = the whole deal's chain; a row id = that tract's.
  const [tractId, setTractId] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const data = await api(`/api/title-chart/session?token=${encodeURIComponent(token || '')}`);
        if (!live) return;
        setSession(data);
        setError('');
      } catch (err) {
        if (!live) return;
        setError(err.message || 'This link could not be opened.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [token]);

  // The two calls SavedDiagram makes, routed through the token instead of
  // through a session. Same shapes it expects back, so the editor itself does
  // not know the difference — including that a conflict is a conflict and not
  // an error to retry.
  const loadDoc = useCallback(async ({ interestEstimateId }) => {
    try {
      const data = await api(`/api/title-chart/doc?token=${encodeURIComponent(token || '')}`
        + `&interestEstimateId=${encodeURIComponent(interestEstimateId || '')}`);
      return { data: data.diagram };
    } catch (err) {
      return { error: err };
    }
  }, [token]);

  const saveDoc = useCallback(async ({ id, doc, version }) => {
    try {
      const data = await api('/api/title-chart/doc', {
        method: 'POST',
        body: { token, id, doc, version, interestEstimateId: tractId },
      });
      return { data: data.diagram };
    } catch (err) {
      if (err.status === 409) return { conflict: true };
      return { error: err };
    }
  }, [token, tractId]);

  const tracts = useMemo(() => session?.tracts || [], [session]);

  if (loading) {
    return <Shell><p style={{ color: '#475569', fontSize: '0.9rem' }}>Opening the chart…</p></Shell>;
  }
  if (error) {
    return (
      <Shell>
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          border: '1px solid #fecaca', background: '#fef2f2', padding: '0.9rem 1rem',
        }}>
          <AlertTriangle size={18} style={{ color: '#b91c1c', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 800, color: '#7f1d1d', fontSize: '0.9rem' }}>{error}</div>
            <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 4, lineHeight: 1.5 }}>
              Links expire, and they can be withdrawn. Ask Paraclete to send a new one.
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  const deal = session?.deal || {};
  return (
    <Shell>
      <header style={{ marginBottom: '0.9rem' }}>
        <div style={{
          fontSize: '0.62rem', fontWeight: 800, letterSpacing: '1px',
          textTransform: 'uppercase', color: '#64748b',
        }}>
          Confirmation title · chain of title
        </div>
        <h1 style={{ margin: '2px 0 0', fontSize: '1.3rem', fontWeight: 800, color: '#0f172a' }}>
          {deal.ownerName || 'Deal'}
        </h1>
        <div style={{ fontSize: '0.82rem', color: '#475569', marginTop: 2 }}>
          {[deal.county ? `${deal.county} County` : '', deal.state].filter(Boolean).join(', ')}
          {session?.agentName ? ` · for ${session.agentName}` : ''}
        </div>
        {/* Said plainly, because an agent used to sending a drawing back
            needs to know this one is already where it is going. */}
        <p style={{
          margin: '0.7rem 0 0', padding: '0.55rem 0.7rem', background: '#eff6ff',
          border: '1px solid #bfdbfe', fontSize: '0.78rem', color: '#1e3a8a', lineHeight: 1.5,
        }}>
          This is Paraclete&rsquo;s own chart for this deal, not a copy — your work saves here and
          they see it as you go. Nothing needs sending back.
        </p>
      </header>

      {/* Which chain. One per tract, plus the deal as a whole, matching the
          anchor the app uses — so a chart drawn here is the chart that opens
          against that tract in Paraclete. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: '0.8rem' }}>
        <TractTab active={tractId === null} onClick={() => setTractId(null)} label="Whole deal" />
        {tracts.map((tract) => (
          <TractTab
            key={tract.id}
            active={tractId === tract.id}
            onClick={() => setTractId(tract.id)}
            label={tractLabel(tract)}
            status={tract.paracleteStatus}
          />
        ))}
      </div>

      <div style={{ border: '1px solid #cbd5e1', background: '#fff', minHeight: 520 }}>
        <SavedDiagram
          key={`${token}:${tractId || 'deal'}`}
          dealId={deal.id}
          interestEstimateId={tractId}
          title={deal.ownerName || ''}
          loadDoc={loadDoc}
          saveDoc={saveDoc}
        />
      </div>
    </Shell>
  );
}

function TractTab({ active, onClick, label, status = null }) {
  // Paraclete's own working assumption about the land, shown rather than
  // used to hide it: a tract we marked sold is exactly the one whose chain
  // decides whether the owner had it to sell.
  const muted = status === 'sold' || status === 'ignored';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '0.35rem 0.6rem', borderRadius: 0, cursor: 'pointer',
        border: `1px solid ${active ? NAVY : '#cbd5e1'}`,
        background: active ? NAVY : '#ffffff',
        color: active ? '#ffffff' : (muted ? '#94a3b8' : '#0f172a'),
        fontSize: '0.74rem', fontWeight: active ? 700 : 500,
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {label}
      {muted ? (
        <span style={{
          fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.4px',
          opacity: 0.85, fontWeight: 700,
        }}>
          {status === 'sold' ? 'Paraclete: sold' : 'Paraclete: ignored'}
        </span>
      ) : null}
    </button>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{
        background: NAVY, color: '#fff', padding: '0.6rem 1rem',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <MapIcon size={15} />
        <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>Paraclete — Title Chart</span>
      </div>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '1.1rem 1rem 2rem' }}>
        {children}
      </div>
    </div>
  );
}
